import {
  IUserSubscriptionApiCall,
  IRestApiService,
  SubscriptionPaymentLinkResponse,
  TariffPlansResponse,
  DetailedSubscriptionDataResponse,
  SubscriptionDataResponse,
  SubscriptionEditRequest
} from '../types';
import { UserSubscriptionTracker } from '../services/analytics/apiTrackers';

class UserSubscriptionApiCall implements IUserSubscriptionApiCall {
  private restApiService: IRestApiService;

  constructor(restApiService: IRestApiService) {
    this.restApiService = restApiService;
  }
  getLinkForSubscriptionPayment(ownerId: string, subscriptionData: any): Promise<SubscriptionPaymentLinkResponse> {
    const startTime = Date.now();

    return this.restApiService.post<SubscriptionPaymentLinkResponse>(
      `user-subscription/owner/${ownerId}`,
      subscriptionData
    )
      .then((result) => {
        UserSubscriptionTracker.trackGetLinkForSubscriptionPayment({
          ownerId,
          subscriptionData,
          success: true,
          duration: Date.now() - startTime,
          result
        });
        return result;
      })
      .catch((error) => {
        UserSubscriptionTracker.trackGetLinkForSubscriptionPayment({
          ownerId,
          subscriptionData,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime
        });
        throw error;
      });
  }

  getTariffPlans(): Promise<TariffPlansResponse> {
    const startTime = Date.now();

    return this.restApiService.get<TariffPlansResponse>(`user-subscription/tariff-plans`)
      .then((result) => {
        UserSubscriptionTracker.trackGetTariffPlans({
          success: true,
          duration: Date.now() - startTime,
          result
        });
        return result;
      })
      .catch((error) => {
        UserSubscriptionTracker.trackGetTariffPlans({
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime
        });
        throw error;
      });
  }

  getDetailedSubscriptionData(ownerId: string): Promise<DetailedSubscriptionDataResponse> {
    const startTime = Date.now();

    return this.restApiService.get<DetailedSubscriptionDataResponse>(`user-subscription/owner/${ownerId}/detailed`)
      .then((result) => {
        UserSubscriptionTracker.trackGetDetailedSubscriptionData({
          ownerId,
          success: true,
          duration: Date.now() - startTime,
          result
        });
        return result;
      })
      .catch((error) => {
        UserSubscriptionTracker.trackGetDetailedSubscriptionData({
          ownerId,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime
        });
        throw error;
      });
  }

  getSubscriptionData(ownerId: string): Promise<SubscriptionDataResponse> {
    const startTime = Date.now();

    return this.restApiService.get<SubscriptionDataResponse>(`user-subscription/owner/${ownerId}`)
      .then((result) => {
        UserSubscriptionTracker.trackGetSubscriptionData({
          ownerId,
          success: true,
          duration: Date.now() - startTime,
          result
        });
        return result;
      })
      .catch((error) => {
        UserSubscriptionTracker.trackGetSubscriptionData({
          ownerId,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime
        });
        throw error;
      });
  }

  editSubscription(ownerId: string, subscriptionData: SubscriptionEditRequest): Promise<any> {
    const startTime = Date.now();

    return this.restApiService.put(`user-subscription/owner/${ownerId}`, subscriptionData)
      .then((result) => {
        UserSubscriptionTracker.trackEditSubscription({
          ownerId,
          subscriptionData,
          success: true,
          duration: Date.now() - startTime
        });
        return result;
      })
      .catch((error) => {
        UserSubscriptionTracker.trackEditSubscription({
          ownerId,
          subscriptionData,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime
        });
        throw error;
      });
  }

  cancelSubscription(ownerId: string): Promise<any> {
    const startTime = Date.now();

    return this.restApiService.post(`user-subscription/owner/${ownerId}/cancel`)
      .then((result) => {
        UserSubscriptionTracker.trackCancelSubscription({
          ownerId,
          success: true,
          duration: Date.now() - startTime,
          result
        });
        return result;
      })
      .catch((error) => {
        UserSubscriptionTracker.trackCancelSubscription({
          ownerId,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime
        });
        throw error;
      });
  }

  resumeSubscription(ownerId: string): Promise<any> {
    const startTime = Date.now();

    return this.restApiService.post(`user-subscription/owner/${ownerId}/resume`)
      .then((result) => {
        UserSubscriptionTracker.trackResumeSubscription({
          ownerId,
          success: true,
          duration: Date.now() - startTime,
          result
        });
        return result;
      })
      .catch((error) => {
        UserSubscriptionTracker.trackResumeSubscription({
          ownerId,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime
        });
        throw error;
      });
  }

  payInvoice(ownerId: string, invoiceId: string): Promise<any> {
    const startTime = Date.now();

    return this.restApiService.post(`user-subscription/owner/${ownerId}/invoice/${invoiceId}/pay`)
      .then((result) => {
        UserSubscriptionTracker.trackPayInvoice({
          ownerId,
          invoiceId,
          success: true,
          duration: Date.now() - startTime,
          result
        });
        return result;
      })
      .catch((error) => {
        UserSubscriptionTracker.trackPayInvoice({
          ownerId,
          invoiceId,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime
        });
        throw error;
      });
  }

  getSubscriptionInfo(ownerId: string): Promise<any> {
    const startTime = Date.now();

    return this.restApiService.get(`user-subscription/owner/${ownerId}/subscription-info`)
      .then((result) => {
        UserSubscriptionTracker.trackGetSubscriptionInfo({
          ownerId,
          success: true,
          duration: Date.now() - startTime,
          result
        });
        return result;
      })
      .catch((error) => {
        UserSubscriptionTracker.trackGetSubscriptionInfo({
          ownerId,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime
        });
        throw error;
      });
  }

  getUniqueClientsCount(ownerId: string, dateFrom: string, dateTo: string): Promise<{clientCount: number}> {
    return this.restApiService.post<{clientCount: number}>(
      `user-subscription/owner/${ownerId}/find-uniq-clients`,
      { dateFrom, dateTo }
    );
  }


}

export default UserSubscriptionApiCall;
